import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { X, ZoomOut, Maximize2, Minimize2, Download, ChevronDown } from 'lucide-react';
import { useTheme } from '@app/theme/ThemeContext';
import { useTimezone } from '@app/contexts/useTimezone';
import { PlottableForecast, ModelId } from '@app/pages/forecast/types';
import { VARIABLES } from '@app/components/common/constants';

import { FORECAST_MODELS, ForecastModel } from '@app/common/static-data/models';
import { ForecastService } from '@app/api/forecasting';
import { JuaForecastResponse } from '@app/api/forecasting/types';
import * as echarts from 'echarts';
import { useDemo } from '@app/components/demo/DemoProvider';

// Custom chart config for LocationPopover with better space utilization
function buildPopoverChartConfig({
  isDark,
  axisColor,
  sortedModels,
  variable,
  yAxisRange,
  series,
  formatInUserTimezone,
  isFullscreen = false,
}: any): echarts.EChartsOption {
  const gridConfig = isFullscreen
    ? { left: '3%', right: '3%', bottom: '8%', top: '12%', containLabel: true }
    : { left: '8%', right: '8%', bottom: '12%', top: '6%', containLabel: true };

  return {
    backgroundColor: 'transparent',
    grid: gridConfig,
    tooltip: {
      trigger: 'axis',
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      borderColor: isDark ? '#374151' : '#E5E7EB',
      textStyle: {
        color: isDark ? '#F9FAFB' : '#111827',
        fontSize: 11,
      },
      formatter: (params: any) => {
        if (!params || params.length === 0) return '';
        const time = formatInUserTimezone(params[0].value[0], 'MMM dd, HH:mm');
        let result = `<div style="font-size: 11px; font-weight: 500; margin-bottom: 6px;">${time}</div>`;
        params.forEach((param: any) => {
          if (param.value[1] !== '-' && param.value[1] !== undefined) {
            result += `<div style="font-size: 11px; color: ${param.color}">
              ${param.seriesName}: ${param.value[1].toFixed(1)} ${variable.displayUnit}
            </div>`;
          }
        });
        return result;
      },
    },
    legend: {
      data: sortedModels.map((modelId: string) => {
        const model = FORECAST_MODELS.find((m) => m.id === modelId);
        return model?.label || modelId;
      }),
      textStyle: {
        color: axisColor,
        fontSize: 11,
      },
      top: isFullscreen ? '2%' : '1%',
      itemGap: 12,
      selectedMode: true,
    },
    dataZoom: [
      {
        type: 'slider' as const,
        show: true,
        xAxisIndex: [0],
        bottom: '2%',
        height: 14,
        borderColor: 'transparent',
        backgroundColor: isDark ? 'rgba(55, 65, 81, 0.3)' : 'rgba(243, 244, 246, 0.8)',
        fillerColor: isDark ? 'rgba(236, 72, 153, 0.3)' : 'rgba(236, 72, 153, 0.2)',
        textStyle: {
          color: axisColor,
          fontSize: 10,
        },
        handleStyle: {
          color: '#EC4899',
          borderColor: '#EC4899',
          borderWidth: 1,
        },
        moveHandleStyle: {
          color: isDark ? '#6B7280' : '#9CA3AF',
        },
        selectedDataBackground: {
          lineStyle: { color: 'transparent' },
          areaStyle: { color: 'transparent' },
        },
        dataBackground: {
          lineStyle: { color: 'transparent' },
          areaStyle: { color: 'transparent' },
        },
        start: 0,
        end: 100,
        filterMode: 'none' as const,
      },
      {
        type: 'inside' as const,
        xAxisIndex: [0],
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        filterMode: 'none' as const,
      },
    ],
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: (value: number) => {
          const date = new Date(value);
          return formatInUserTimezone(date.toISOString(), 'MMM dd');
        },
        color: axisColor,
        fontSize: 10,
        hideOverlap: true,
      },
      splitLine: {
        show: false,
      },
      axisLine: {
        lineStyle: {
          color: axisColor,
        },
      },
    },
    yAxis: {
      type: 'value',
      name: variable.displayUnit,
      nameLocation: 'middle',
      nameGap: 35,
      nameTextStyle: {
        color: axisColor,
        fontSize: 11,
      },
      min: yAxisRange.min,
      max: yAxisRange.max,
      axisLabel: {
        color: axisColor,
        fontSize: 10,
        formatter: (value: number) => {
          // Don't use 'k' format for pressure values (hPa) - they need to be readable
          if (variable.displayUnit === 'hPa') {
            return value.toFixed(0); // Show whole numbers for pressure
          }
          // For other units, use 'k' format for large values
          if (Math.abs(value) >= 1000) {
            return (value / 1000).toFixed(1) + 'k';
          }
          return value.toFixed(1);
        },
      },
      splitLine: {
        show: false,
      },
    },
    series,
  };
}
import { getModelColor } from '@app/pages/forecast/utils/colorUtils';
import { kelvinToCelsius } from '@app/pages/market-aggregates/utils/chartHelpers';

// Helper function to convert forecast response to PlottableForecast
function convertToPlottableForecast(
  response: JuaForecastResponse,
  modelId: string,
): PlottableForecast {
  const { forecast } = response;
  const { lead_times, times, points } = forecast;
  const { returned_latlon, ...variables } = points[0];
  const { lat: latitude, lon: longitude } = returned_latlon;

  // Convert time data
  let time: Date[];
  if (lead_times) {
    time = lead_times.map((t) => new Date(t));
  } else if (times) {
    time = times.map((t) => new Date(t));
  } else {
    throw Error('Response should include `lead_times` or `times`');
  }

  // Process variables data
  const data: PlottableForecast['data'] = {};
  Object.entries(variables).forEach(([key, value]) => {
    if (typeof value === 'object' && Array.isArray(value)) {
      (data as any)[key] = value as number[];
    }
  });

  // Convert temperature from Kelvin to Celsius
  if (data['air_temperature_at_height_level_2m']) {
    data['air_temperature_at_height_level_2m'] =
      data['air_temperature_at_height_level_2m'].map(kelvinToCelsius);
  }
  if (data['dew_point_temperature_at_height_level_2m']) {
    data['dew_point_temperature_at_height_level_2m'] =
      data['dew_point_temperature_at_height_level_2m'].map(kelvinToCelsius);
  }

  // Convert percentage values from fraction (0-1) to percentage (0-100)
  const percentageVariables = [
    'cloud_area_fraction_at_entire_atmosphere_high_type',
    'cloud_area_fraction_at_entire_atmosphere_medium_type',
    'cloud_area_fraction_at_entire_atmosphere_low_type',
    'relative_humidity_at_height_level_2m',
  ];

  percentageVariables.forEach((variable) => {
    if ((data as any)[variable]) {
      (data as any)[variable] = (data as any)[variable].map((value: number) => value * 100);
    }
  });

  // Convert pressure from Pa to hPa (divide by 100)
  if (data['air_pressure_at_mean_sea_level']) {
    data['air_pressure_at_mean_sea_level'] = data['air_pressure_at_mean_sea_level'].map(
      (value: number) => value / 100,
    );
  }

  // Convert ept2e ensemble values (divide by 30 to get average from sum of 30 members)
  if (modelId === 'ept2e') {
    Object.keys(data).forEach((variable) => {
      if ((data as any)[variable] && Array.isArray((data as any)[variable])) {
        (data as any)[variable] = (data as any)[variable].map((value: number) => value / 30);
      }
    });
  }

  return {
    latitude,
    longitude,
    time,
    data,
  };
}

interface LocationPopoverProps {
  currentModel: ModelId;
  coordinates: { lat: number; lon: number };
  onClose: () => void;
}

export function LocationPopover({ currentModel, coordinates, onClose }: LocationPopoverProps) {
  const { theme } = useTheme();
  const { formatInUserTimezone } = useTimezone();
  const { isDemo } = useDemo();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#D1D5DB' : '#6B7280';

  // Local state for data and loading
  const [data, setData] = useState<Record<ModelId, PlottableForecast> | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  // Dropdown states
  const [isVariableDropdownOpen, setIsVariableDropdownOpen] = useState<boolean>(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState<boolean>(false);
  const variableDropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  // Track active requests to prevent duplicates

  const currentModelList = useMemo(() => [currentModel], [currentModel]);

  // Model selection state - default to current model
  const [selectedModels, setSelectedModels] = useState<ForecastModel[]>(currentModelList);

  // Variable selection state - default to air temperature
  const [selectedVariable, setSelectedVariable] = useState<string>(
    'air_temperature_at_height_level_2m',
  );

  // Helper function to compare model arrays
  const modelsAreEqual = useCallback(
    (models1: ForecastModel[], models2: ForecastModel[]): boolean => {
      if (models1.length !== models2.length) return false;
      return models1.every((model, index) => model === models2[index]);
    },
    [],
  );

  // Optimized model selection handler
  const handleModelSelectionChange = useCallback(
    (newModels: ForecastModel[]) => {
      setSelectedModels((current) => {
        if (!modelsAreEqual(newModels, current)) {
          return newModels;
        } else {
          return current;
        }
      });
    },
    [modelsAreEqual],
  );

  // Get variables that support time series
  const timeSeriesVariables = useMemo(
    () =>
      VARIABLES.filter((variable) => variable.showTimeSeries).map((variable) => ({
        id: variable.juaId,
        label: variable.label,
        description: variable.description,
      })),
    [],
  );

  // Load forecast data for specific models
  const loadModelData = useCallback(
    async (modelIds: ForecastModel[], coordinates: { lat: number; lon: number }) => {
      if (modelIds.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const promises = modelIds.map(async (modelId) => {
          const response = await ForecastService.getLatestAtPoint(
            modelId,
            coordinates.lat,
            coordinates.lon,
            0,
            168,
            false,
            [],
            isDemo,
          );
          return { modelId, forecast: convertToPlottableForecast(response, modelId) };
        });

        const newData: Record<ModelId, PlottableForecast> = {};
        const results = await Promise.all(promises);
        results.forEach(({ modelId, forecast }) => {
          newData[modelId] = forecast;
        });
        setData(newData);
      } catch (error) {
        console.error('Failed to load forecast data for models:', modelIds, error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Load data for selected models
  useEffect(() => {
    if (selectedModels.length > 0) {
      loadModelData(selectedModels, coordinates);
    }
  }, [coordinates, selectedModels, loadModelData]);

  // Prepare chart data for selected variable across selected models
  const chartData = useMemo(() => {
    if (!data || selectedModels.length === 0) {
      return null;
    }

    const variable = VARIABLES.find((v) => v.juaId === selectedVariable);
    if (!variable) return null;

    const series: any[] = [];
    const allValidValues: number[] = [];

    selectedModels.forEach((modelId, index) => {
      const modelData = data[modelId];
      if (
        !modelData ||
        !modelData.data[selectedVariable as keyof typeof modelData.data] ||
        !modelData.time
      ) {
        return;
      }

      const variableData = modelData.data[selectedVariable as keyof typeof modelData.data];
      const timeData = modelData.time;

      if (!variableData || !timeData || variableData.length !== timeData.length) {
        return;
      }

      // Create series data for this model
      const seriesData = timeData.map((time, i) => [time.getTime(), variableData[i]]);

      // Add valid values for y-axis range calculation
      const validValues = variableData.filter(
        (val: number) => val !== undefined && val !== null && !isNaN(val),
      );
      allValidValues.push(...validValues);

      // Get model name and color
      const model = FORECAST_MODELS.find((m) => m.id === modelId);
      const modelName = model?.label || modelId;
      const modelColor = getModelColor(modelId, index, theme);

      series.push({
        name: modelName,
        type: 'line' as const,
        data: seriesData,
        lineStyle: {
          color: modelColor,
          width: 2.5,
        },
        itemStyle: {
          color: modelColor,
        },
        symbol: 'none',
        smooth: 0.2,
        emphasis: {
          focus: 'series' as const,
          itemStyle: {
            borderWidth: 2,
            borderColor: isDark ? '#1F2937' : '#FFFFFF',
          },
        },
      });
    });

    if (series.length === 0 || allValidValues.length === 0) {
      return null;
    }

    // Calculate y-axis range from all models
    const minVal = Math.min(...allValidValues);
    const maxVal = Math.max(...allValidValues);
    const padding = (maxVal - minVal) * 0.1;

    let yAxisMin = minVal - padding;
    let yAxisMax = maxVal + padding;

    // Special handling for percentage variables - ensure 0-100% range
    if (variable.displayUnit === '%') {
      yAxisMin = Math.max(0, yAxisMin); // Never go below 0%
      yAxisMax = Math.min(100, yAxisMax); // Never go above 100%
      // Ensure at least 5% padding for readability
      if (yAxisMax - yAxisMin < 10) {
        const center = (yAxisMin + yAxisMax) / 2;
        yAxisMin = Math.max(0, center - 5);
        yAxisMax = Math.min(100, center + 5);
      }
    }

    // Special handling for precipitation - never show negative values
    if (variable.displayUnit === 'mm') {
      yAxisMin = Math.max(0, yAxisMin); // Never go below 0 mm
      // Ensure some minimum range for readability when values are very small
      if (yAxisMax - yAxisMin < 0.5) {
        yAxisMax = Math.max(0.5, yAxisMax);
      }
    }

    // Special handling for wind speed - never show negative values
    if (variable.displayUnit === 'm s⁻¹') {
      yAxisMin = Math.max(0, yAxisMin); // Never go below 0 m/s
    }

    // Special handling for solar radiation - never show negative values
    if (variable.displayUnit === 'J m⁻²') {
      yAxisMin = Math.max(0, yAxisMin); // Never go below 0 J/m²
    }

    // Determine interval based on variable type and round min/max to multiples
    const isTemperature = variable.displayUnit === '°C' || variable.displayUnit === 'K';
    const isWindSpeed = variable.displayUnit === 'm s⁻¹';
    const interval = isTemperature ? 5 : isWindSpeed ? 2 : undefined;

    if (interval) {
      yAxisMin = Math.floor(yAxisMin / interval) * interval;
      yAxisMax = Math.ceil(yAxisMax / interval) * interval;

      // Re-apply minimum constraints after rounding
      if (variable.displayUnit === 'm s⁻¹' && yAxisMin < 0) {
        yAxisMin = 0;
      }
    }

    const yAxisRange = {
      min: yAxisMin,
      max: yAxisMax,
    };

    return {
      sortedModels: selectedModels,
      variable,
      yAxisRange,
      series,
    };
  }, [data, selectedModels, selectedVariable, theme, isDark]);

  // Chart instance management with proper timing
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef<boolean>(false);

  // Use callback ref to capture the DOM element and initialize chart
  const chartRef = useCallback((node: HTMLDivElement | null) => {
    if (node && !isInitialized.current) {
      // Initialize chart only once when DOM element becomes available
      chartInstance.current = echarts.init(node, undefined, {
        renderer: 'canvas',
      });
      isInitialized.current = true;
    }
  }, []);

  // Update chart when data changes
  useEffect(() => {
    if (!chartData || !chartInstance.current) {
      return;
    }

    const { sortedModels, variable, yAxisRange, series } = chartData;

    const option = buildPopoverChartConfig({
      isDark,
      axisColor,
      sortedModels,
      variable,
      yAxisRange,
      series,
      formatInUserTimezone,
      isFullscreen,
    });

    chartInstance.current.clear();
    chartInstance.current.setOption(option);
  }, [chartData, isDark, axisColor, formatInUserTimezone, isFullscreen]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Chart event handlers
  const resetZoom = useCallback(() => {
    if (chartInstance.current) {
      chartInstance.current.dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100,
      });
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const downloadChart = useCallback(() => {
    if (!chartInstance.current) return;

    const selectedVar = timeSeriesVariables.find((v) => v.id === selectedVariable);
    const varLabel = selectedVar?.label || selectedVariable;
    const filename = `weather-forecast-${varLabel.toLowerCase().replace(/\s+/g, '-')}-${coordinates.lat.toFixed(2)}-${coordinates.lon.toFixed(2)}.png`;

    const url = chartInstance.current.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
    });

    const link = document.createElement('a');
    link.download = filename;
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedVariable, timeSeriesVariables, coordinates, isDark]);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Handle click outside for dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        variableDropdownRef.current &&
        !variableDropdownRef.current.contains(event.target as Node)
      ) {
        setIsVariableDropdownOpen(false);
      }
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setIsModelDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
      isInitialized.current = false;
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`absolute bottom-20 left-4 right-4 z-30 ${isFullscreen ? 'inset-0 h-full flex flex-col' : 'h-72'} ${
        isDark ? 'bg-gray-800' : 'bg-white'
      } shadow-sm border rounded-xl ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
    >
      {/* Header */}
      <div className="p-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Location: {coordinates.lat.toFixed(3)}°, {coordinates.lon.toFixed(3)}°
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-3">
            {/* Variable Selector */}
            <div className="flex items-center gap-2" ref={variableDropdownRef}>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                Variable
              </label>
              <div className="relative">
                <button
                  onClick={() => setIsVariableDropdownOpen(!isVariableDropdownOpen)}
                  className="flex items-center gap-2 px-2 sm:px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 justify-between whitespace-nowrap min-w-0"
                >
                  <span className="truncate">
                    {timeSeriesVariables.find((v) => v.id === selectedVariable)?.label ||
                      selectedVariable}
                  </span>
                  <ChevronDown
                    className={`h-3 w-3 transition-transform flex-shrink-0 ${isVariableDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isVariableDropdownOpen && (
                  <div className="absolute bottom-full mb-1 left-0 min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    {timeSeriesVariables.map((variable) => (
                      <button
                        key={variable.id}
                        onClick={() => {
                          setSelectedVariable(variable.id);
                          setIsVariableDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          variable.id === selectedVariable
                            ? 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                            : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <span className="truncate block">{variable.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Model Selector */}
            <div className="flex items-center gap-2" ref={modelDropdownRef}>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap hidden sm:inline">
                Models
              </label>
              <div className="relative">
                <button
                  onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                  className="flex items-center gap-2 px-2 sm:px-3 py-1 text-xs bg-gray-100 dark:bg-gray-800 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 justify-between whitespace-nowrap min-w-0"
                >
                  <span className="truncate">
                    {selectedModels.length === 1
                      ? FORECAST_MODELS.find((m) => m.id === selectedModels[0])?.label ||
                        selectedModels[0]
                      : `${selectedModels.length} models`}
                  </span>
                  <ChevronDown
                    className={`h-3 w-3 transition-transform flex-shrink-0 ${isModelDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isModelDropdownOpen && (
                  <div className="absolute bottom-full mb-1 left-0 min-w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                    {FORECAST_MODELS.map((model) => (
                      <button
                        key={model.id}
                        onClick={() => {
                          const newSelectedModels = selectedModels.includes(model.id)
                            ? selectedModels.filter((id) => id !== model.id)
                            : [...selectedModels, model.id];
                          handleModelSelectionChange(newSelectedModels);
                        }}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          selectedModels.includes(model.id)
                            ? 'bg-pink-50 dark:bg-pink-900/20 text-pink-600 dark:text-pink-400 font-medium'
                            : 'text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate">{model.label}</span>
                          {selectedModels.includes(model.id) && (
                            <div className="w-2 h-2 bg-pink-500 rounded-full ml-2 flex-shrink-0" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Chart Controls */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={downloadChart}
                className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
                aria-label="Download Chart"
                title="Download PNG"
              >
                <Download className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
                aria-label="Toggle Fullscreen"
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? (
                  <Minimize2 className="w-3.5 h-3.5" />
                ) : (
                  <Maximize2 className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={resetZoom}
                className="p-1.5 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded group relative"
                aria-label="Reset Zoom"
                title="Reset Zoom"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
                title="Close"
              >
                <X className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className={`relative ${isFullscreen ? 'flex-1' : 'h-48'}`}>
        {loading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div
              className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin ${
                isDark ? 'border-pink-400' : 'border-pink-500'
              }`}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Loading forecast data...
            </div>
          </div>
        ) : !data || Object.keys(data).length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">No data available</div>
          </div>
        ) : !chartData ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              No data available for selected variable
            </div>
          </div>
        ) : (
          <div ref={chartRef} className="w-full h-full" />
        )}
      </div>
    </div>
  );
}
